import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as BackIcon,
  Check as CheckIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  EngineeringOutlined as ChargeIcon,
  HistoryOutlined as HistoryIcon,
  Inventory2Outlined as MatiereIcon,
  LocalShippingOutlined as TruckIcon,
  LockOutlined as LockIcon,
  PaymentsOutlined as PayIcon,
  PrintOutlined as PrintIcon,
  Undo as UndoIcon,
  BuildOutlined as TravailIcon,
  PrecisionManufacturingOutlined as ProdIcon,
  OpenInNew as OpenIcon,
  RocketLaunchOutlined as LaunchIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as TodoIcon,
} from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { usePermissions } from '../../../contexts/PermissionContext';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { auditLogsAPI, comptoirMatieresAPI, comptoirPaiementsAPI, comptoirProductionAPI, comptoirServicesAPI } from '../../../services/api';
import { useComptoirService, useComptoirServiceHistory, useComptoirServiceProduction, useComptoirSuiviService, useInvalidateComptoir } from '../../../hooks/queries/useComptoir';
import { ComptoirLivraison, ComptoirOffre, ComptoirPaiement, ComptoirPaiementImpact, ComptoirServiceDetail as ServiceDetail } from '../../../types/comptoir';
import OffreWizard from '../components/OffreWizard';
import EncaisserDialog from '../components/EncaisserDialog';
import { PaiementDeleteDialog, PaiementEditDialog } from '../components/PaiementDialogs';
import LivrerDialog from '../components/LivrerDialog';
import { LancerDialog, RetourDialog, ServiceEditDialog, retourOptions } from '../components/ServiceDialogs';
import TravauxModifiesAlert from '../components/TravauxModifiesAlert';
import MatiereEntreprisePanel from '../components/MatiereEntreprisePanel';
import { ProdBar, StatutChip, slotLabel } from '../components/ProductionDialogs';
import { COLOR, ClientAvatar, ETAT_TONE, Fact, PAIEMENT_TONE, PANEL_SX, Panel, StateChip, dh, fDate, fmtPhone, num, primaryBtnSx } from '../ui';

const ComptoirServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const sid = Number(id);
  const navigate = useNavigate();
  const { data: s, isLoading, error } = useComptoirService(sid);
  const { data: history = [] } = useComptoirServiceHistory(sid);
  const { data: prod } = useComptoirServiceProduction(sid);
  const invalidate = useInvalidateComptoir();
  const qc = useQueryClient();
  const { alertState, showError, showSuccess, showConfirm, showWarning, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { hasPermission, canAccessModule } = usePermissions();
  const canProduction = canAccessModule('Production');
  const canSuivi = hasPermission('Comptoir', 'view', 'Suivi de production');
  const canSuiviAdd = hasPermission('Comptoir', 'add', 'Suivi de production');
  const { data: suivi } = useComptoirSuiviService(sid, canSuivi);
  const canSrv = (op: 'add' | 'edit' | 'delete') => hasPermission('Comptoir', op, 'Services & Projets');
  const canOffreEdit = hasPermission('Comptoir', 'edit', 'Offres commerciales');

  const [payOpen, setPayOpen] = useState(false);
  const [docsMenu, setDocsMenu] = useState<HTMLElement | null>(null);
  const [livOpen, setLivOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [retourOpen, setRetourOpen] = useState(false);
  const [lancerOpen, setLancerOpen] = useState(false);
  const peutForcer = true; // anyone may launch without avance, with a motif (same rule as the backend)
  const [wizardOpen, setWizardOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showAllHist, setShowAllHist] = useState(false);
  const [payEdit, setPayEdit] = useState<{ p: ComptoirPaiement; impact: ComptoirPaiementImpact; action: 'edit' | 'delete' } | null>(null);
  const [payChecking, setPayChecking] = useState(false);

  /** Modifier / Supprimer a voucher: first ask the server what depends on it. */
  const checkPaiement = async (paiementId: number, action: 'edit' | 'delete') => {
    setPayChecking(true);
    try {
      const [p, impact] = await Promise.all([comptoirPaiementsAPI.get(paiementId), comptoirPaiementsAPI.impact(paiementId)]);
      if (action === 'edit' && impact.blocages_modification.length) {
        showWarning(impact.blocages_modification.join(' '), `Modifier ${p.reference}`);
      } else {
        setPayEdit({ p, impact, action });
      }
    } catch (e: any) { showError(extractErrorMessage(e)); } finally { setPayChecking(false); }
  };

  if (isLoading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !s) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/comptoir/services')}>Services & Projets</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error ? extractErrorMessage(error) : 'Service introuvable'}</Alert>
      </Box>
    );
  }

  const closed = s.etat === 'cloture';
  const reste = num(s.reste);
  const restAliv = s.travaux.some((t) => num(t.reste_a_livrer) > 0);
  // the Production panel lives under the 'suivi' key: a payment (first avance → bureau d'études),
  // a livraison or a type change must refresh it too
  const refresh = () => { invalidate(); qc.invalidateQueries({ queryKey: ['suivi', 'comptoir-service', sid] }); };

  /* ---------- documents ---------- */
  const printDevis = (offre: ComptoirOffre, name: string) => {
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirDevisPdfReactPdf')).getComptoirDevisPDFBlob(offre),
      fileName: `${name}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirOffre', offre.id, `Aperçu ${name}`);
  };
  const printBon = async (paiementId: number, reference: string) => {
    try {
      const p = await comptoirPaiementsAPI.get(paiementId);
      openPreview({
        getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
        fileName: `Bon_${reference}.pdf`,
      });
      auditLogsAPI.logPdfDownload('ComptoirPaiement', paiementId, `Aperçu bon de paiement - ${reference}`);
    } catch (e: any) { showError(extractErrorMessage(e)); }
  };
  // prototype V8 bons: bon d'avance, bon général, fiche de travail (with the étapes when readable)
  const printV8 = async (kind: 'avance' | 'general' | 'travail') => {
    const pdfs = await import('../../../utils/comptoirBonsPdfReactPdf');
    const name = { avance: `Avance_${s.reference}`, general: `Bon_general_${s.reference}`, travail: `Travail_${s.reference}` }[kind];
    const label = { avance: "bon d'avance", general: 'bon général', travail: 'fiche de travail' }[kind];
    openPreview({
      getBlob: async () => {
        if (kind === 'avance') return pdfs.getBonAvancePDFBlob(s);
        if (kind === 'general') return pdfs.getBonGeneralPDFBlob(s);
        const [prod, mats] = await Promise.all([
          comptoirProductionAPI.service(s.id).catch(() => null),
          comptoirMatieresAPI.list(s.id).catch(() => []),
        ]);
        return pdfs.getFicheTravailPDFBlob(s, prod, mats);
      },
      fileName: `${name}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirService', s.id, `Aperçu ${label} - ${s.reference}`);
  };
  const printBL = (lv: ComptoirLivraison, svc: ServiceDetail = s) => {
    openPreview({
      getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonLivraisonPDFBlob(svc, lv),
      fileName: `BL_${lv.reference}.pdf`,
    });
    auditLogsAPI.logPdfDownload('ComptoirLivraison', lv.id, `Aperçu bon de livraison - ${lv.reference}`);
  };

  const doClose = () => {
    const what = s.nature === 'Projet' ? 'le projet' : 'le service';
    showConfirm(`${s.reference} est livré et soldé. Clôturer ${what} ? (Réversible avec « Étape précédente ».)`, async () => {
      setClosing(true);
      try {
        await comptoirServicesAPI.cloturer(s.id);
        refresh();
        showSuccess(`${s.reference} clôturé`);
      } catch (e: any) { showError(extractErrorMessage(e)); } finally { setClosing(false); }
    }, { title: `Clôturer ${what} ?`, confirmText: 'Clôturer' });
  };

  const markRecue = async (mid: number, name: string) => {
    try {
      await comptoirServicesAPI.matiereRecue(s.id, mid);
      refresh();
      showSuccess(`${name} : reçue`);
    } catch (e: any) { showError(extractErrorMessage(e)); }
  };

  /* ---------- progress + next step ---------- */
  // Production (Gestion Planning + Suivi): done when every suivi step is checked. A job delivered
  // without the atelier following it in Suivi (quick counter work) counts as done, « non suivie ».
  const delivered = ['livre', 'cloture'].includes(s.etat);
  const prodDone = !!prod?.termine || delivered;
  const lance = !!s.lance_le || delivered;
  // launched but the plan isn't validated yet: the job is at the bureau d'études (prototype step)
  const auBE = lance && !delivered && !!s.be && ['attente', 'conception'].includes(s.be.statut);
  const prodSub = !prod ? undefined : prod.termine ? '100 %' : delivered ? 'Non suivie' : !lance ? 'À lancer'
    : auBE ? "Bureau d'études" : prod.planifie ? `${prod.progress} %` : 'À planifier';
  const steps: [string, boolean, string?][] = [
    ['Offre acceptée', true],
    ['Avance reçue', num(s.paye) > 0],
    ['Production', prodDone, prodSub],
    [s.etat === 'livre_partiel' ? 'Livré en partie' : 'Livré', ['livre', 'cloture'].includes(s.etat)],
    ['Soldé', reste <= 0],
    ['Clôturé', closed],
  ];
  const cur = steps.findIndex(([, ok]) => !ok);

  let nextText = '';
  let nextBtn: React.ReactNode = null;
  const btn = (label: string, icon: React.ReactNode, onClick: () => void, allowed: boolean) =>
    allowed ? <Button variant="contained" startIcon={icon} onClick={onClick} sx={primaryBtnSx}>{label}</Button> : null;
  if (!closed) {
    if (num(s.paye) <= 0 && !lance) {
      nextText = "Encaisser l'avance du client, puis lancer en production. Sans avance, vous pouvez lancer quand même (avec un motif).";
      nextBtn = canSrv('add') ? (
        <Stack direction="row" spacing={1}>
          {btn("Encaisser l'avance", <PayIcon />, () => setPayOpen(true), true)}
          <Button variant="outlined" startIcon={<LaunchIcon />} onClick={() => setLancerOpen(true)}
            sx={{ borderColor: COLOR, color: COLOR, whiteSpace: 'nowrap' }}>Lancer sans avance</Button>
        </Stack>
      ) : null;
    } else if (!lance) {
      nextText = "Avance reçue. Lancez le projet en production : les travaux, la matière du client et vos instructions partent au bureau d'études.";
      nextBtn = btn('Lancer en production', <LaunchIcon />, () => setLancerOpen(true), canSrv('add'));
    } else if (auBE && restAliv) {
      nextText = s.be?.statut === 'conception'
        ? "Au bureau d'études : le plan est en cours de conception. Une fois validé, le projet peut être planifié."
        : "Au bureau d'études : en attente du plan technique. Une fois validé, le projet peut être planifié.";
      nextBtn = canProduction ? (
        <Button variant="contained" startIcon={<OpenIcon />} sx={primaryBtnSx} onClick={() => navigate('/production/bureau-etudes')}>
          Ouvrir le bureau d'études
        </Button>
      ) : null;
    } else if (restAliv && prod && !prod.termine) {
      nextText = prod.planifie
        ? `En production (${prod.progress} %)${prod.etape_en_cours ? ` — étape en cours : ${prod.etape_en_cours}` : ''}. Ce qui est prêt peut déjà être livré.`
        : 'À planifier en production (Gestion Planning). Ce qui est prêt peut déjà être livré.';
      nextBtn = canProduction ? (
        <Button variant="contained" startIcon={<OpenIcon />} sx={primaryBtnSx}
          onClick={() => navigate(prod.planifie ? '/production/suivi' : '/production/planning')}>
          {prod.planifie ? 'Ouvrir le suivi' : 'Ouvrir le planning'}
        </Button>
      ) : null;
    } else if (restAliv) {
      nextText = s.etat === 'livre_partiel' ? 'Livrez le reste de la commande.' : 'Avance reçue. Livrez les travaux au client quand ils sont prêts (une livraison partielle est possible).';
      nextBtn = btn('Livrer', <TruckIcon />, () => setLivOpen(true), canSrv('add'));
    } else if (reste > 0) {
      nextText = `Livré. Reste à encaisser : ${dh(reste)}.`;
      nextBtn = btn('Encaisser le reste', <PayIcon />, () => setPayOpen(true), canSrv('add'));
    } else {
      nextText = `Livré et soldé. Vous pouvez clôturer ${s.nature === 'Projet' ? 'le projet' : 'le service'}.`;
      nextBtn = btn('Clôturer', <LockIcon />, doClose, canSrv('edit'));
    }
  }
  const pendingMat = s.offre.matieres.filter((m) => !m.date_reception);

  const ligneEtat = (t: ServiceDetail['travaux'][number]) => {
    const q = num(t.quantite), l = num(t.livre);
    if (l >= q) return <StateChip small label="Livré" t={ETAT_TONE.livre} />;
    if (l > 0) return <StateChip small label={`Livré ${l}/${q}`} t={ETAT_TONE.livre_partiel} />;
    return <StateChip small label="À livrer" t={{ bg: 'transparent', fg: COLOR, outlined: true }} />;
  };

  const hist = showAllHist ? history : history.slice(0, 8);

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/comptoir/services')} sx={{ mb: 1, color: 'text.secondary' }}>Services & Projets</Button>

      {/* ---------- header ---------- */}
      {/* The title gets its own line (it can be long); chips and actions share the next one */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        <Box component="span" sx={{ fontFamily: 'monospace', color: COLOR }}>{s.reference}</Box> · {s.titre}
      </Typography>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ lg: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={s.nature} size="small" variant="outlined" />
          <StateChip label={s.etat_com_label} t={ETAT_TONE[s.etat_com]} />
          <Typography variant="caption" color={s.arrets ? 'error' : 'text.secondary'}>Production : {s.etat_prod_label}</Typography>
          <StateChip label={s.paiement_label} t={PAIEMENT_TONE[s.paiement]} />
          {s.priorite === 'urgente' && !closed && <Chip label="Urgent" size="small" color="error" variant="outlined" />}
          {s.en_retard && <Chip label="En retard" size="small" color="error" />}
          {s.version > 1 && <Chip label={`Version ${s.version}`} size="small" />}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ lg: 'flex-end' }}
          sx={{ '& .MuiButton-root': { whiteSpace: 'nowrap' } }}>
          {canSrv('edit') && !closed && <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>Modifier</Button>}
          {canOffreEdit && !closed && <Button variant="outlined" startIcon={<TravailIcon />} onClick={() => setWizardOpen(true)}>Modifier les travaux</Button>}
          {canSrv('add') && reste > 0 && <Button variant="outlined" startIcon={<PayIcon />} onClick={() => setPayOpen(true)}>Encaisser</Button>}
          {canSrv('add') && !closed && restAliv && <Button variant="outlined" startIcon={<TruckIcon />} onClick={() => setLivOpen(true)}>Livrer</Button>}
          {canSrv('edit') && retourOptions(s).length > 0 && <Button variant="outlined" color="warning" startIcon={<UndoIcon />} onClick={() => setRetourOpen(true)}>Étape précédente</Button>}
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={(e) => setDocsMenu(e.currentTarget)}>Documents</Button>
          <Menu anchorEl={docsMenu} open={!!docsMenu} onClose={() => setDocsMenu(null)}>
            <MenuItem onClick={() => { setDocsMenu(null); printDevis(s.offre, `Devis_${s.offre_reference}`); }}>Devis</MenuItem>
            <MenuItem onClick={() => { setDocsMenu(null); printV8('avance'); }}>Bon d'avance</MenuItem>
            <MenuItem onClick={() => { setDocsMenu(null); printV8('general'); }}>Bon général</MenuItem>
            <MenuItem onClick={() => { setDocsMenu(null); printV8('travail'); }}>Fiche de travail (atelier)</MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* ---------- progress ---------- */}
      <Paper variant="outlined" sx={{ ...PANEL_SX, p: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1 }}>
          {steps.map(([label, ok, sub], i) => (
            <Stack key={label} direction="row" spacing={1} alignItems="center">
              <Box sx={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                bgcolor: ok ? 'success.main' : i === cur ? COLOR : 'action.selected', color: ok || i === cur ? '#fff' : 'text.secondary',
              }}>
                {ok ? <CheckIcon sx={{ fontSize: 16 }} /> : i + 1}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: i === cur ? 700 : 500, color: ok || i === cur ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
                {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{sub}</Typography>}
              </Box>
            </Stack>
          ))}
        </Box>
        {nextText && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
            sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: alpha(COLOR, 0.06), border: `1px solid ${alpha(COLOR, 0.25)}` }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" sx={{ color: COLOR, fontWeight: 700, lineHeight: 1.5 }}>Prochaine étape</Typography>
              <Typography variant="body2">{nextText}</Typography>
            </Box>
            {nextBtn}
          </Stack>
        )}
        {pendingMat.length > 0 && !closed && (
          <Alert severity="warning" icon={<MatiereIcon />} sx={{ mt: 1.5 }}>
            Matière du client pas encore reçue : {pendingMat.map((m) => m.designation).join(', ')}.
          </Alert>
        )}
      </Paper>

      {/* ---------- facts ---------- */}
      <Paper variant="outlined" sx={{ ...PANEL_SX, p: 2 }}>
        <Grid container spacing={2}>
          <Fact label="Client">
            <Stack direction="row" spacing={1} alignItems="center">
              <ClientAvatar name={s.client_nom} size={26} />
              <span>{s.client_nom}{s.client_surnom ? ` « ${s.client_surnom} »` : ''}</span>
            </Stack>
          </Fact>
          <Fact label="Téléphone"><span style={{ fontFamily: 'monospace' }}>{fmtPhone(s.client_telephone)}</span></Fact>
          <Fact label="Offre commerciale"><span style={{ fontFamily: 'monospace' }}>{s.offre_reference}</span>{s.version > 1 ? ` · v${s.version}` : ''}</Fact>
          <Fact label="Accepté le">{fDate(s.accepte_le)}</Fact>
          <Fact label="Date souhaitée client">{fDate(s.date_souhaitee)}</Fact>
          <Fact label="Livraison promise (deadline)" danger={s.en_retard}>{fDate(s.deadline)}{s.en_retard ? ' · en retard' : ''}</Fact>
          <Fact label="Chargé(s) de projet">
            {s.charges_projet.length
              ? <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>{s.charges_projet.map((c) => <Chip key={c.id} icon={<ChargeIcon />} label={c.full_name} size="small" />)}</Stack>
              : '—'}
          </Fact>
          <Fact label="Livraison">{s.livraisons.filter((l) => !l.annule).map((l) => `${l.reference} · ${fDate(l.date)}`).join(', ') || 'Non livré'}</Fact>
          {s.description && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Note interne</Typography><Typography variant="body2">{s.description}</Typography></Grid>}
        </Grid>
      </Paper>

      {/* ---------- travaux ---------- */}
      <Panel title="Travaux demandés" icon={<TravailIcon />} count={`${s.travaux.length} travail(x)${s.version > 1 ? ` · version ${s.version}` : ''}`}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell><TableCell>Travail</TableCell><TableCell align="right">Qté</TableCell>
                <TableCell align="right">Livré</TableCell><TableCell align="right">Reste à livrer</TableCell><TableCell>État</TableCell>
                <TableCell align="right">P.U.</TableCell><TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Désignations détaillées: a header row with the group's total, then its lines n.1, n.2…
                const groupes = new Map((s.offre.groupes || []).map((g) => [g.id, g]));
                let n = 0, j = 0, current: number | null = null;
                return s.travaux.flatMap((t) => {
                  const g = t.groupe_id != null ? groupes.get(t.groupe_id) : undefined;
                  const head = [];
                  let label: string;
                  if (!g) { current = null; label = String(++n); }
                  else {
                    if (current !== g.id) {
                      current = g.id; j = 0; n += 1;
                      head.push(
                        <TableRow key={`g${g.id}`} sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700 }}>{n}</TableCell>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{g.libelle}</Typography>
                            {g.description && <Typography variant="caption" color="text.secondary">{g.description}</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap' }}>{dh(g.total)}</TableCell>
                        </TableRow>,
                      );
                    }
                    label = `${n}.${++j}`;
                  }
                  return [
                    ...head,
                    <TableRow key={t.id}>
                      <TableCell sx={{ pl: g ? 4 : undefined, whiteSpace: 'nowrap' }}>{label}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.designation}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.type_service}{t.description ? ` · ${t.description}` : ''}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{num(t.quantite)} {t.unite}</TableCell>
                      <TableCell align="right">{num(t.livre)}</TableCell>
                      <TableCell align="right">{num(t.reste_a_livrer)}</TableCell>
                      <TableCell>{ligneEtat(t)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{dh(t.prix_unitaire)}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{dh(t.total)}</TableCell>
                    </TableRow>,
                  ];
                });
              })()}
            </TableBody>
            <TableFooter>
              {num(s.remise_pct) > 0 && (
                <>
                  <TableRow><TableCell colSpan={7} align="right">Total des travaux</TableCell><TableCell align="right" sx={{ fontFamily: 'monospace' }}>{dh(s.total_travaux)}</TableCell></TableRow>
                  <TableRow><TableCell colSpan={7} align="right">Remise {num(s.remise_pct)} %</TableCell><TableCell align="right" sx={{ fontFamily: 'monospace' }}>− {dh(s.remise_montant)}</TableCell></TableRow>
                </>
              )}
              <TableRow>
                <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'text.primary' }}>{dh(s.total)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
        {s.versions.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">Versions précédentes :</Typography>
            {s.versions.slice().reverse().map((v) => (
              <Tooltip key={v.n} title={v.motif}>
                <Chip size="small" variant="outlined" label={`v${v.n} · ${v.total != null ? dh(v.total) : '—'} · ${fDate(v.created_at)}`} />
              </Tooltip>
            ))}
          </Stack>
        )}
      </Panel>

      {/* ---------- production (read-only: Gestion Planning + Suivi) ---------- */}
      {prod && (
        <Panel title="Production" icon={<ProdIcon />} count={prod.planifie ? `${prod.progress} %` : delivered ? 'Non suivie' : !lance ? 'À lancer' : 'À planifier'}
          action={!lance ? (canSrv('add') ? (
            <Button size="small" startIcon={<LaunchIcon fontSize="small" />} sx={{ color: COLOR }} onClick={() => setLancerOpen(true)}>
              Lancer en production
            </Button>
          ) : undefined) : canProduction && (prod.planifie || !delivered) ? (
            <Button size="small" endIcon={<OpenIcon fontSize="small" />} sx={{ color: COLOR }}
              onClick={() => navigate(prod.planifie ? '/production/suivi' : '/production/planning')}>
              {prod.planifie ? 'Voir le suivi' : 'Planifier'}
            </Button>
          ) : undefined}>
          {!lance && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5 }}>
              Pas encore lancé en production{num(s.paye) > 0 ? '' : " : le lancement attend l'avance"}. Le lancement l'envoie au bureau d'études avec vos instructions.
            </Typography>
          )}
          {s.lance_le && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ px: 2, pt: 1.5 }}>
              Lancé le {fDate(s.lance_le)}{s.lance_par ? ` par ${s.lance_par}` : ''}{s.lance_motif ? ` · sans avance : ${s.lance_motif}` : ''}
            </Typography>
          )}
          <TravauxModifiesAlert revoir={s.revoir} sx={{ mx: 2, mt: 1.5 }} />
          {prod.be && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1.5 }}>
              <Typography variant="body2" color="text.secondary">Bureau d'études :</Typography>
              <Chip size="small" label={prod.be.label} variant="outlined"
                color={prod.be.statut === 'valide' ? 'success' : prod.be.statut === 'sans_plan' ? 'default' : 'warning'} />
              {canProduction && (
                <Button size="small" sx={{ color: COLOR }} onClick={() => navigate('/production/bureau-etudes')}>Ouvrir</Button>
              )}
            </Stack>
          )}
          {!prod.planifie ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {delivered
                ? `Livré sans planification ni suivi en atelier (Gestion Planning / Suivi).`
                : `${prod.code} n'est pas encore planifié dans Gestion Planning.`}
            </Typography>
          ) : (
            <>
              <Box sx={{ px: 2, pt: 1.5 }}>
                <LinearProgress variant="determinate" value={Math.min(100, prod.progress)}
                  sx={{ height: 8, borderRadius: 4, bgcolor: alpha(COLOR, 0.12), '& .MuiLinearProgress-bar': { bgcolor: prod.termine ? 'success.main' : COLOR } }} />
              </Box>
              {prod.planning.map((e, i) => (
                <Typography key={i} variant="body2" sx={{ px: 2, pt: 1 }}>
                  Planifié le <b>{fDate(e.date)}</b>{e.debut ? ` · ${e.debut}–${e.fin || ''}` : ''}
                  {e.atelier ? ` · ${e.atelier}` : ''}{e.responsables.length ? ` · ${e.responsables.join(', ')}` : ''}
                  {e.statut !== 'en_cours' && <Chip size="small" sx={{ ml: 1, height: 20 }}
                    color={e.statut === 'termine' ? 'success' : 'error'} variant="outlined"
                    label={e.statut === 'termine' ? 'Terminé' : 'Bloqué'} />}
                </Typography>
              ))}
              <Box sx={{ p: 2, pt: 1 }}>
                {prod.phases.map((ph) => (
                  <Stack key={ph.nom} direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 2 }} sx={{ py: 0.75, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 200 }}>{ph.nom}</Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {ph.etapes.map((st) => (
                        <Tooltip key={st.nom} title={st.fait ? `Fait${st.le ? ` le ${fDate(st.le)}` : ''}${st.par ? ` par ${st.par}` : ''}` : 'À faire'}>
                          <Chip size="small" variant="outlined" label={st.nom}
                            icon={st.fait ? <DoneIcon /> : <TodoIcon />}
                            color={st.fait ? 'success' : 'default'}
                            sx={{ '& .MuiChip-icon': { fontSize: 16 } }} />
                        </Tooltip>
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Box>
            </>
          )}
        </Panel>
      )}

      {/* ---------- étapes de fabrication (Comptoir › Suivi de production) ---------- */}
      {suivi && (suivi.etapes.length > 0 || (!s.cloture_le && s.etat !== 'livre')) && (
        <Panel title="Étapes de fabrication" icon={<ProdIcon />}
          count={suivi.etapes.length ? `${suivi.nb_terminees}/${suivi.etapes.length} terminée(s)` : 'Aucune étape'}
          action={suivi.etapes.length ? (
            <Button size="small" endIcon={<OpenIcon fontSize="small" />} sx={{ color: COLOR }}
              onClick={() => navigate(`/comptoir/production?service=${s.id}`)}>Ouvrir le suivi</Button>
          ) : canSuiviAdd ? (
            <Button size="small" sx={{ color: COLOR }} onClick={() => navigate(`/comptoir/production?service=${s.id}&nouvelle=1`)}>Ajouter les étapes</Button>
          ) : undefined}>
          {!suivi.etapes.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Découpez le travail en étapes (découpe, pliage, soudage…) pour suivre les pièces réalisées, conformes et à refaire.
            </Typography>
          ) : (
            <Box sx={{ px: 2, py: 0.5 }}>
              {suivi.etapes.map((e) => (
                <Stack key={e.id} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={{ xs: 0.5, md: 2 }}
                  sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                  <Box sx={{ minWidth: 240, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.ordre}. {e.designation}{e.travail_label ? ` · ${e.travail_label}` : ''}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.machine ? `${e.machine.code} · ` : ''}{e.responsable?.full_name || 'Non affecté'} · {slotLabel(e)}
                    </Typography>
                  </Box>
                  <ProdBar e={e} compact />
                  <Box sx={{ minWidth: 150 }}><StatutChip e={e} /></Box>
                </Stack>
              ))}
            </Box>
          )}
        </Panel>
      )}

      <Grid container spacing={2.5}>
        {/* ---------- money ---------- */}
        <Grid item xs={12} md={7}>
          <Panel title="Situation financière" icon={<PayIcon />}>
            <Grid container sx={{ p: 2 }}>
              {[['Prix total', dh(s.total), 'text.primary'], ['Encaissé', dh(s.paye), 'success.main'], ['Reste', dh(s.reste), reste > 0 ? 'error.main' : 'success.main']].map(([k, v, c]) => (
                <Grid item xs={4} key={k}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 700, color: c }}>{v}</Typography>
                </Grid>
              ))}
              <Grid item xs={12}>
                <LinearProgress variant="determinate" value={num(s.total) > 0 ? Math.min(100, num(s.paye) / num(s.total) * 100) : 0}
                  sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: alpha(COLOR, 0.12), '& .MuiLinearProgress-bar': { bgcolor: reste <= 0 ? 'success.main' : COLOR } }} />
              </Grid>
            </Grid>
            <Divider />
            {s.paiements.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucun paiement sur ce service.</Typography>
            ) : s.paiements.map((p) => (
              <Stack key={`${p.paiement_id}`} direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2"><Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR }}>{p.reference}</Box> · {p.type}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fDate(p.date)} · {p.mode}{p.reference_paiement ? ` · ${p.reference_paiement}` : ''} · reste après : {dh(p.reste_apres)}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{dh(p.montant)}</Typography>
                <Tooltip title="Imprimer le bon de paiement">
                  <IconButton size="small" onClick={() => printBon(p.paiement_id, p.reference)} sx={{ color: COLOR }}><PrintIcon fontSize="small" /></IconButton>
                </Tooltip>
                {canSrv('edit') && (
                  <Tooltip title="Modifier le paiement">
                    <span>
                      <IconButton size="small" onClick={() => checkPaiement(p.paiement_id, 'edit')} disabled={payChecking} sx={{ color: COLOR }} aria-label={`Modifier ${p.reference}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                {canSrv('delete') && (
                  <Tooltip title="Supprimer le paiement">
                    <span>
                      <IconButton size="small" color="error" onClick={() => checkPaiement(p.paiement_id, 'delete')} disabled={payChecking} aria-label={`Supprimer ${p.reference}`}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Panel>
        </Grid>

        {/* ---------- client material ---------- */}
        <Grid item xs={12} md={5}>
          <Panel title="Matière fournie par le client" icon={<MatiereIcon />} count={s.offre.matieres.length}>
            {s.offre.matieres.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Le client n'a déposé ni matière ni pièce pour ce service.</Typography>
            ) : s.offre.matieres.map((m) => (
              <Stack key={m.id} direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.designation}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[m.quantite != null ? `${num(m.quantite)} ${m.unite || ''}` : '', m.dimensions, m.observations].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                </Box>
                {m.date_reception
                  ? <StateChip small label={`Reçue le ${fDate(m.date_reception)}`} t={{ bg: 'transparent', fg: '#2E7D32', outlined: true }} />
                  : (canSrv('edit') && !closed
                    ? <Button size="small" variant="outlined" startIcon={<CheckIcon />} onClick={() => markRecue(m.id, m.designation)}>Reçue</Button>
                    : <StateChip small label="À déposer" t={{ bg: 'transparent', fg: '#ED6C02', outlined: true }} />)}
              </Stack>
            ))}
          </Panel>
        </Grid>
      </Grid>

      {/* ---------- company material, from the ERP stock (prototype V8) ---------- */}
      {canSuivi && <MatiereEntreprisePanel serviceId={s.id} closed={closed} onError={showError} />}

      {/* ---------- deliveries ---------- */}
      <Panel title="Livraisons" icon={<TruckIcon />} count={`${s.livre_pct} % livré`}>
        {s.livraisons.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucune livraison pour l'instant.</Typography>
        ) : s.livraisons.slice().reverse().map((l) => (
          <Stack key={l.id} direction="row" alignItems="center" spacing={1.5}
            sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 }, opacity: l.annule ? 0.65 : 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, color: COLOR, textDecoration: l.annule ? 'line-through' : 'none' }}>{l.reference}</Box>
                {' · '}{fDate(l.date)}{l.annule && <Chip label="Annulée" size="small" color="warning" sx={{ ml: 1, height: 20 }} />}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {l.lignes.map((x) => `${num(x.quantite)} × ${x.designation}`).join(', ')}
                {l.annule && l.motif_annulation ? ` · annulée : ${l.motif_annulation}` : l.note ? ` · ${l.note}` : ''}
              </Typography>
            </Box>
            <Tooltip title="Imprimer le bon de livraison">
              <IconButton size="small" onClick={() => printBL(l)} sx={{ color: COLOR }}><PrintIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Panel>

      {/* ---------- history ---------- */}
      <Panel title="Historique" icon={<HistoryIcon />} count={history.length}
        action={history.length > 8 ? <Button size="small" onClick={() => setShowAllHist(!showAllHist)}>{showAllHist ? 'Réduire' : 'Tout afficher'}</Button> : undefined}>
        {hist.length === 0 ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Aucun événement.</Typography> : hist.map((h) => (
          <Stack key={h.id} direction="row" spacing={2} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>{new Date(h.date).toLocaleString('fr-FR')}</Typography>
            <Box>
              <Typography variant="body2">{h.description}</Typography>
              <Typography variant="caption" color="text.secondary">{h.user_name || '—'}</Typography>
            </Box>
          </Stack>
        ))}
      </Panel>

      {/* ---------- dialogs ---------- */}
      <EncaisserDialog
        open={payOpen} clientId={s.client_id} clientNom={s.client_nom} serviceId={s.id}
        onClose={() => setPayOpen(false)}
        onSaved={(p) => {
          setPayOpen(false);
          refresh();
          openPreview({
            getBlob: async () => (await import('../../../utils/comptoirBonsPdfReactPdf')).getBonPaiementPDFBlob(p),
            fileName: `Bon_${p.reference}.pdf`,
          });
        }}
      />
      <PaiementEditDialog
        open={payEdit?.action === 'edit'} paiement={payEdit?.p || null} impact={payEdit?.impact || null}
        onClose={() => setPayEdit(null)}
        onSaved={(p) => { setPayEdit(null); refresh(); showSuccess(`${p.reference} modifié`); }}
      />
      <PaiementDeleteDialog
        open={payEdit?.action === 'delete'} paiement={payEdit?.p || null} impact={payEdit?.impact || null}
        onClose={() => setPayEdit(null)}
        onDeleted={(ref) => { setPayEdit(null); refresh(); showSuccess(`${ref} supprimé`); }}
      />
      <LivrerDialog
        open={livOpen} service={s} onClose={() => setLivOpen(false)}
        onSaved={async (lv) => {
          setLivOpen(false);
          refresh();
          printBL(lv, await comptoirServicesAPI.get(s.id));
        }}
      />
      <ServiceEditDialog open={editOpen} service={s} onClose={() => setEditOpen(false)}
        onSaved={(n) => {
          setEditOpen(false);
          refresh();
          showSuccess(n.reference !== s.reference ? `${n.nature} enregistré : ${s.reference} devient ${n.reference}` : `${n.reference} enregistré`);
        }} />
      <LancerDialog open={lancerOpen} service={s} peutForcer={peutForcer} onClose={() => setLancerOpen(false)}
        onSaved={(x) => { setLancerOpen(false); refresh(); showSuccess(`${x.reference} lancé en production : il part au bureau d'études`); }} />
      <RetourDialog open={retourOpen} service={s} onClose={() => setRetourOpen(false)}
        onSaved={() => { setRetourOpen(false); refresh(); showSuccess("Retour enregistré dans l'historique"); }} />
      <OffreWizard
        open={wizardOpen} offre={s.offre} onClose={() => setWizardOpen(false)}
        onSaved={(o, _isNew, print) => {
          setWizardOpen(false);
          refresh();
          if (print) printDevis(o, `Devis_${o.reference}`);
          else showSuccess(`Travaux de ${s.reference} enregistrés`);
        }}
      />
      {closing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0 }} />}
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default ComptoirServiceDetail;
